// The browser and native Linux IDE share these bounded, lossless report rules.
// All stored reports are copies of scalar data, independent of the debuggee.
export const MEMORY_MAX_BYTES = 1024 * 1024;
export const MEMORY_MAX_EXPORT_BYTES = 4 * MEMORY_MAX_BYTES;
export const MEMORY_MAX_TYPE_ROWS = 512;
export const MEMORY_CAPABILITY = "memorySnapshot";
const UINT64_MAX = (1n << 64n) - 1n;
const encoder = new TextEncoder();
const validatedSnapshots = new WeakSet();
const counts = ["activeCount", "deletedRetainedCount", "accountedBytes", "peakBytes"];

function fail(message) { throw new Error(`Invalid memory report: ${message}`); }
function record(value, name) {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail(name);
  return value;
}
function string(value, name, limit = 256) {
  if (typeof value !== "string" || value.length > limit || encoder.encode(value).length > limit) fail(name);
  return value;
}
function boolean(value, name) {
  if (typeof value !== "boolean") fail(name);
  return value;
}
function decimal(value, name) {
  if (typeof value !== "string" || !/^(0|[1-9][0-9]{0,19})$/.test(value) || BigInt(value) > UINT64_MAX) fail(name);
  return value;
}
function rows(value, name, limit, convert) {
  if (!Array.isArray(value) || value.length > limit) fail(`${name} row limit`);
  const ids = new Set();
  return Object.freeze(value.map((row) => {
    const result = convert(record(row, name));
    if (!result.id || ids.has(result.id)) fail(`${name} duplicate/empty identity`);
    ids.add(result.id);
    return Object.freeze(result);
  }));
}
function metric(row) {
  const result = {};
  for (const key of ["id", "name", "scope", "basis", "coverageRevision"]) result[key] = string(row[key], key);
  for (const key of ["id", "scope", "basis", "coverageRevision"]) if (!result[key]) fail(`empty ${key}`);
  result.available = boolean(row.available, "available");
  return result;
}

export function validateSnapshot(input) {
  if (input && typeof input === "object" && validatedSnapshots.has(input)) return input;
  if (typeof input === "string") {
    if (input.length > MEMORY_MAX_BYTES || encoder.encode(input).length > MEMORY_MAX_BYTES) fail("response exceeds 1 MiB");
    try { input = JSON.parse(input); } catch { fail("malformed JSON"); }
  }
  const source = record(input, "snapshot");
  if (source.schemaVersion !== 1) fail("unsupported schema version");
  const result = { schemaVersion: 1 };
  for (const key of ["runtimeId", "domain", "captureState"]) result[key] = string(source[key], key);
  for (const key of ["runtimeId", "domain", "captureState"]) if (!result[key]) fail(`empty ${key}`);
  for (const key of ["runId", "contextId", "sequence", "timestamp", "omittedTypeRows"]) result[key] = decimal(source[key], key);
  for (const key of ["available", "complete"]) result[key] = boolean(source[key], key);
  result.error = string(source.error, "error", 2048);
  if (source.errorTruncated !== undefined) result.errorTruncated = boolean(source.errorTruncated, "errorTruncated");
  if (result.complete && result.omittedTypeRows !== "0") fail("complete report has omitted Types");
  const total = record(source.totals, "totals");
  result.totals = Object.freeze({ accountedBytes: decimal(total.accountedBytes, "total accounted bytes"),
    peakBytes: decimal(total.peakBytes, "total peak bytes") });
  result.categories = rows(source.categories, "categories", 11, (row) => {
    const output = metric(row);
    for (const key of [...counts, "entries", "capacity", "logicalBytes"]) output[key] = decimal(row[key], key);
    if (row.retiredSlots !== undefined) output.retiredSlots = decimal(row.retiredSlots, "retiredSlots");
    return output;
  });
  result.types = rows(source.types, "types", MEMORY_MAX_TYPE_ROWS, (row) => {
    const output = metric(row);
    output.nameAvailable = boolean(row.nameAvailable, "nameAvailable");
    if (row.nameTruncated !== undefined) output.nameTruncated = boolean(row.nameTruncated, "nameTruncated");
    for (const key of [...counts, "reusableSlots", "allocatedSlots"]) output[key] = decimal(row[key], key);
    if (output.available && BigInt(output.allocatedSlots) !== BigInt(output.activeCount) +
      BigInt(output.deletedRetainedCount) + BigInt(output.reusableSlots)) fail("Type pool invariant");
    return output;
  });
  result.supplemental = rows(source.supplemental, "supplemental", 64, (row) => {
    const output = metric(row);
    for (const key of ["unit", "relationship"]) output[key] = string(row[key], key);
    output.limitation = string(row.limitation, "limitation", 2048);
    if (row.limitationTruncated !== undefined) output.limitationTruncated = boolean(row.limitationTruncated, "limitationTruncated");
    output.peakAvailable = boolean(row.peakAvailable, "peakAvailable");
    if ((!output.available && row.current !== null) || (!(output.available && output.peakAvailable) && row.peak !== null)) {
      fail("unavailable metric must be null");
    }
    output.current = output.available ? decimal(row.current, "current") : null;
    output.peak = output.available && output.peakAvailable ? decimal(row.peak, "peak") : null;
    for (const key of ["sampleTimestamp", "sampleFrame"]) output[key] = decimal(row[key], key);
    return output;
  });
  if (encoder.encode(JSON.stringify(result)).length > MEMORY_MAX_BYTES) fail("response exceeds 1 MiB");
  Object.freeze(result);
  validatedSnapshots.add(result);
  return result;
}

export function formatBytes(value) {
  if (value === null || value === undefined) return "Unavailable";
  const number = BigInt(value), negative = number < 0n, magnitude = negative ? -number : number;
  const divisor = magnitude >= 1048576n ? 1048576n : magnitude >= 1024n ? 1024n : 1n;
  const unit = divisor === 1048576n ? "MiB" : divisor === 1024n ? "KiB" : "B";
  const tenth = (magnitude % divisor) * 10n / divisor;
  return `${negative ? "−" : ""}${magnitude / divisor}${divisor > 1n ? `.${tenth}` : ""} ${unit}`;
}

const difference = (before, current) => (BigInt(current) - BigInt(before)).toString();
const sameCoverage = (before, current) => before.scope === current.scope && before.basis === current.basis &&
  before.coverageRevision === current.coverageRevision && before.unit === current.unit;

export function compareSnapshots(baseline, current) {
  baseline = validateSnapshot(baseline);
  current = validateSnapshot(current);
  const reason = baseline.schemaVersion !== current.schemaVersion ? "The schema versions differ." :
    baseline.runId !== current.runId || baseline.contextId !== current.contextId ? "Choose snapshots from the same run and context." :
    baseline.runtimeId !== current.runtimeId || baseline.domain !== current.domain ? "The runtime or context domains differ." :
    !baseline.available || !current.available ? "Runtime accounting is unavailable." : "";
  const result = { compatible: !reason, reason, complete: baseline.complete && current.complete,
    baselineSequence: baseline.sequence, currentSequence: current.sequence, totals: null,
    categories: Object.freeze([]), types: Object.freeze([]), supplemental: Object.freeze([]) };
  if (reason) return Object.freeze(result);
  const compareRows = (kind) => {
    const before = new Map(baseline[kind].map((row) => [row.id, row]));
    const after = new Map(current[kind].map((row) => [row.id, row]));
    const typeParentBefore = baseline.categories.find((row) => row.id === "types");
    const typeParentAfter = current.categories.find((row) => row.id === "types");
    const parentCompatible = typeParentBefore?.available && typeParentAfter?.available && sameCoverage(typeParentBefore, typeParentAfter);
    return Object.freeze([...new Set([...before.keys(), ...after.keys()])].map((id) => {
      const left = before.get(id), right = after.get(id), row = right || left;
      let rowReason = "";
      if ((!left || !right) && !(kind === "types" && parentCompatible &&
        (left ? current.complete : baseline.complete))) rowReason = "Missing or omitted detail; change is unavailable.";
      else if ((left && !left.available) || (right && !right.available)) rowReason = "Metric is unavailable.";
      else if (left && right && !sameCoverage(left, right)) rowReason = "Metric coverage differs.";
      else if (kind === "types" && !parentCompatible) rowReason = "Type category coverage differs or is unavailable.";
      const countChange = rowReason || kind === "supplemental" ? null : difference(left?.activeCount || "0", right?.activeCount || "0");
      const byteChange = rowReason ? null : kind === "supplemental" ?
        (row.unit === "bytes" ? difference(left.current, right.current) : null) :
        difference(left?.accountedBytes || "0", right?.accountedBytes || "0");
      return Object.freeze({ id, name: row.name, reason: rowReason, countChange, byteChange,
        valueChange: !rowReason && kind === "supplemental" ? difference(left.current, right.current) : null,
        baselinePeak: left?.peakBytes ?? left?.peak ?? null, currentPeak: right?.peakBytes ?? right?.peak ?? null });
    }));
  };
  for (const kind of ["categories", "types", "supplemental"]) result[kind] = compareRows(kind);
  const compatibleTotals = result.categories.every((row) => !row.reason);
  result.totals = Object.freeze({ byteChange: compatibleTotals ? difference(baseline.totals.accountedBytes, current.totals.accountedBytes) : null,
    reason: compatibleTotals ? "" : "Category coverage differs; total change is unavailable.",
    baselinePeak: baseline.totals.peakBytes, currentPeak: current.totals.peakBytes });
  return Object.freeze(result);
}

export class MemoryHistory {
  constructor() { this.clear(); }
  clear() { this.snapshots = []; this.selectedId = null; this.baselineId = null; this.serial = 0; }
  get selected() { return this.snapshots.find((entry) => entry.id === this.selectedId) || null; }
  get baseline() { return this.snapshots.find((entry) => entry.id === this.baselineId) || null; }
  add(input, label = "") {
    const snapshot = validateSnapshot(input);
    const previous = this.snapshots.at(-1)?.snapshot;
    if (previous && (snapshot.runId !== previous.runId || snapshot.contextId !== previous.contextId ||
      snapshot.runtimeId !== previous.runtimeId || BigInt(snapshot.sequence) <= BigInt(previous.sequence))) {
      throw new Error("Memory reply belongs to another run/context or is stale.");
    }
    const id = String(++this.serial);
    const entry = Object.freeze({ id, label: label ? string(label, "snapshot name") : `Snapshot ${id}`, snapshot });
    this.snapshots.push(entry);
    this.selectedId = id;
    if (!this.baselineId) this.baselineId = id;
    let evicted = null;
    if (this.snapshots.length > 8) evicted = this.snapshots.splice(this.snapshots.findIndex((item) => item.id !== this.baselineId), 1)[0];
    return { entry, evicted };
  }
  select(id) { if (!this.snapshots.some((entry) => entry.id === id)) throw new Error("Unknown memory snapshot."); this.selectedId = id; }
  rename(id, label) {
    const name = string(label.trim(), "snapshot name");
    if (!name) throw new Error("A snapshot name is required.");
    const index = this.snapshots.findIndex((entry) => entry.id === id);
    if (index < 0) throw new Error("Unknown memory snapshot.");
    this.snapshots[index] = Object.freeze({ ...this.snapshots[index], label: name });
  }
  setBaseline(id = this.selectedId) {
    if (!this.snapshots.some((entry) => entry.id === id)) throw new Error("Unknown memory snapshot.");
    this.baselineId = id;
  }
  comparison() { return this.selected && this.baseline ? compareSnapshots(this.baseline.snapshot, this.selected.snapshot) : null; }
  exportEnvelope() {
    if (!this.selected) throw new Error("Take a snapshot before exporting.");
    return { format: "blitz3d-memory-report", version: 1,
      labels: { accountedBytes: "Runtime accounted bytes", peakBytes: "Lifetime peak; not interval peak" },
      selected: { label: this.selected.label, snapshot: this.selected.snapshot },
      baseline: this.baseline ? { label: this.baseline.label, snapshot: this.baseline.snapshot } : null,
      differences: this.comparison() };
  }
  exportJson() {
    // At most two bounded reports and a bounded union of their detail rows.
    const json = JSON.stringify(this.exportEnvelope(), null, 2) + "\n";
    if (encoder.encode(json).length > MEMORY_MAX_EXPORT_BYTES) throw new Error("Memory export exceeds the 4 MiB limit.");
    return json;
  }
}

// Local sorting/filtering never asks the runtime to capture again. A count-only
// increase remains visible even when its allocation reused existing capacity.
export function sortedMemoryRows(snapshot, comparison, { kind = "categories", filter = "", sort = "growth" } = {}) {
  const changes = new Map((comparison?.[kind] || []).map((row) => [row.id, row]));
  const needle = filter.toLocaleLowerCase();
  const result = snapshot[kind].map((row) => ({ ...row, change: changes.get(row.id) || null }));
  // A complete detail set can show Types that disappeared since the baseline.
  for (const change of changes.values()) if (!result.some((row) => row.id === change.id)) {
    result.push({ id: change.id, name: change.name, available: !change.reason, activeCount: "0", accountedBytes: "0", change });
  }
  return result.filter((row) => !needle || row.name.toLocaleLowerCase().includes(needle) || row.id.toLocaleLowerCase().includes(needle))
    .sort((left, right) => {
      const compare = (a, b) => a < b ? 1 : a > b ? -1 : 0;
      if (sort === "name") return left.name.localeCompare(right.name);
      if (sort === "count") return compare(BigInt(left.activeCount || "0"), BigInt(right.activeCount || "0"));
      if (sort === "bytes") return compare(BigInt(left.accountedBytes || "0"), BigInt(right.accountedBytes || "0"));
      return compare(BigInt(left.change?.byteChange || "0"), BigInt(right.change?.byteChange || "0")) ||
        compare(BigInt(left.change?.countChange || "0"), BigInt(right.change?.countChange || "0")) || left.name.localeCompare(right.name);
    });
}
